// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2022 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import './styles.scss';
import React, { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import Button from 'antd/lib/button';
import Empty from 'antd/lib/empty';
import Pagination from 'antd/lib/pagination';
import Skeleton from 'antd/lib/skeleton';
import { PlusOutlined } from '@ant-design/icons';

import { CombinedState } from 'reducers';
import { Project } from 'cvat-core-wrapper';
import { getProjectsAsync } from 'actions/projects-actions';
import { importActions } from 'actions/import-actions';
import { t } from 'utils/i18n';
import ContinueRow from './continue-row';
import ProjectCard from './project-card';

const PAGE_SIZE = 12;

// the header's search box lands here: /projects?search=flat
export function projectsSearchPath(search: string): string {
    const query = search.trim();
    return query ? `/projects?search=${encodeURIComponent(query)}` : '/projects';
}

export function searchFromLocation(locationSearch: string): string | null {
    return new URLSearchParams(locationSearch).get('search')?.trim() || null;
}

export default function ProjectsPageComponent(): JSX.Element {
    const dispatch = useDispatch();
    const history = useHistory();
    const fetching = useSelector((state: CombinedState) => state.projects.fetching);
    const projects = useSelector((state: CombinedState) => state.projects.current);
    const count = useSelector((state: CombinedState) => state.projects.count);
    const query = useSelector((state: CombinedState) => state.projects.gettingQuery);
    const importing = useSelector((state: CombinedState) => state.import.projects.backup.importing);
    const search = searchFromLocation(useLocation().search);

    useEffect(() => {
        dispatch(getProjectsAsync({
            page: 1, search, filter: null, sort: '-updated_date',
        }));
    }, [search]);

    const load = (next: { page?: number; search?: string | null }): void => {
        dispatch(getProjectsAsync({ ...query, ...next }));
    };

    let content: React.ReactNode;
    if (fetching && !projects.length) {
        // the first load: the grid's shape, so nothing jumps when the cards arrive
        content = (
            <div className='cvat-projects-grid' aria-busy>
                {Array.from({ length: 8 }, (_, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={index} className='cvat-projects-card'>
                        <div className='cvat-projects-card-preview cvat-preview-skeleton' />
                        <Skeleton active title={false} paragraph={{ rows: 2, width: ['60%', '45%'] }} />
                    </div>
                ))}
            </div>
        );
    } else if (!projects.length) {
        content = (
            <Empty
                className='cvat-projects-empty'
                description={search ? t('No project matches the search') : t('No projects yet. Start with New project.')}
            />
        );
    } else {
        content = (
            <div className={`cvat-projects-grid${fetching ? ' cvat-projects-grid-stale' : ''}`} aria-busy={fetching}>
                {projects.map((project: Project) => <ProjectCard key={project.id} project={project} />)}
            </div>
        );
    }

    return (
        <div className='cvat-projects-page'>
            <div className='cvat-projects-page-content'>
                <ContinueRow />
                <section className='cvat-projects-section' aria-label={t('Projects')}>
                    <div className='cvat-projects-heading'>
                        <h1>{search ? t('Projects matching “{search}”', { search }) : t('Projects')}</h1>
                        <Button
                            type='link'
                            className='cvat-projects-import'
                            loading={importing}
                            onClick={() => dispatch(importActions.openImportBackupModal('project'))}
                        >
                            {t('Import from backup')}
                        </Button>
                        <Button type='primary' icon={<PlusOutlined />} onClick={() => history.push('/projects/create')}>
                            {t('New project')}
                        </Button>
                    </div>
                    {content}
                    {count > PAGE_SIZE && (
                        <Pagination
                            className='cvat-projects-pagination'
                            onChange={(page) => load({ page })}
                            showSizeChanger={false}
                            total={count}
                            pageSize={PAGE_SIZE}
                            current={query.page}
                        />
                    )}
                </section>
            </div>
        </div>
    );
}
